import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";

export const CloudProviderContext = createContext();

const SELECTED_CLOUD_PROVIDER_KEY = "selectedCloudProvider";

const getStoredSelectedProvider = () => {
  try {
    const stored = sessionStorage.getItem(SELECTED_CLOUD_PROVIDER_KEY);
    if (stored === "AWS" || stored === "AZURE") {
      return stored;
    }
  } catch (_) {
    // sessionStorage may be unavailable (private mode / restricted)
  }
  return "AWS";
};

const persistSelectedProvider = (provider) => {
  try {
    sessionStorage.setItem(SELECTED_CLOUD_PROVIDER_KEY, provider);
  } catch (_) {
    // ignore persistence failures
  }
};

export const getAccountCloudType = (account) => {
  const type = account?.accounttype ?? account?.accountType;
  // Treat missing/null accountType as AWS (legacy accounts behave like AWS view)
  if (type == null || type === "null") {
    return "AWS";
  }
  return String(type).toUpperCase();
};

// Explicit type only — null/"null" ignored (for toggle visibility)
export const getExplicitAccountCloudType = (account) => {
  const type = account?.accounttype ?? account?.accountType;
  if (type == null || type === "null") {
    return null;
  }
  const normalized = String(type).toUpperCase();
  return normalized === "AWS" || normalized === "AZURE" ? normalized : null;
};

export const getAccountIdentifier = (account) =>
  String(
    account?.accountNumber ??
      account?.accnumber ??
      account?.account_number ??
      account ??
      ""
  )
    .trim()
    .split(/\s+-\s+/)[0]
    .trim();

const AWS_ACCOUNT_NUMBER_PATTERN = /^\d{12}$/;
const AZURE_SUBSCRIPTION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const inferCloudTypeFromAccountNumber = (accountNumber) => {
  const normalized = String(accountNumber || "").trim();
  if (!normalized) return "AWS";
  if (AZURE_SUBSCRIPTION_ID_PATTERN.test(normalized)) return "AZURE";
  if (AWS_ACCOUNT_NUMBER_PATTERN.test(normalized)) return "AWS";
  return "AWS";
};

export const buildAccountCloudTypeLookup = (accounts = []) => {
  const lookup = new Map();
  if (!Array.isArray(accounts)) {
    return lookup;
  }
  accounts.forEach((account) => {
    const id = getAccountIdentifier(account);
    if (id) {
      const explicit = getExplicitAccountCloudType(account);
      lookup.set(id, explicit ?? inferCloudTypeFromAccountNumber(id));
    }
  });
  return lookup;
};

export const getCloudTypeForAccountRef = (accountOrNumber, cloudTypeLookup) => {
  const id = getAccountIdentifier(accountOrNumber);
  if (!id) return "AWS";

  const explicitType =
    accountOrNumber && typeof accountOrNumber === "object"
      ? getExplicitAccountCloudType(accountOrNumber)
      : null;
  if (explicitType) return explicitType;

  const inferredType = inferCloudTypeFromAccountNumber(id);

  if (cloudTypeLookup?.has(id)) {
    const lookupType = cloudTypeLookup.get(id);
    if (lookupType === "AWS" && inferredType === "AZURE") {
      return "AZURE";
    }
    return lookupType;
  }

  return inferredType;
};

export const matchesSelectedCloudProvider = (
  accountOrNumber,
  selectedProvider,
  cloudTypeLookup
) => {
  if (!selectedProvider) return true;
  return (
    getCloudTypeForAccountRef(accountOrNumber, cloudTypeLookup) ===
    selectedProvider
  );
};

export const filterAccountsByCloudProvider = (
  accounts = [],
  selectedProvider,
  cloudTypeLookup
) =>
  (accounts || []).filter((account) =>
    matchesSelectedCloudProvider(account, selectedProvider, cloudTypeLookup)
  );

export const filterAccountNumbersByCloudProvider = (
  accountNumbers = [],
  selectedProvider,
  cloudTypeLookup
) =>
  (accountNumbers || []).filter((accountNumber) =>
    matchesSelectedCloudProvider(accountNumber, selectedProvider, cloudTypeLookup)
  );

export const filterResourceConfigsByCloudProvider = (
  resourceConfigs,
  selectedProvider,
  cloudTypeLookup
) => {
  if (!resourceConfigs) return resourceConfigs;

  return {
    ...resourceConfigs,
    accounts: filterAccountsByCloudProvider(
      resourceConfigs.accounts,
      selectedProvider,
      cloudTypeLookup
    ),
    accounts_with_budgets: filterAccountsByCloudProvider(
      resourceConfigs.accounts_with_budgets,
      selectedProvider,
      cloudTypeLookup
    ),
    accounts_in_default_resource_group: filterAccountNumbersByCloudProvider(
      resourceConfigs.accounts_in_default_resource_group,
      selectedProvider,
      cloudTypeLookup
    ),
  };
};

export const shouldShowAzurePlaceholderView = (selectedProvider) => {
  return selectedProvider === "AZURE";
};

export const CloudProviderProvider = ({ children }) => {
  const [selectedProvider, setSelectedProvider] = useState(
    getStoredSelectedProvider
  );
  // Accumulate providers only for the active org so provider-scoped account
  // fetches (Compliance/CSPM) do not hide the toggle mid-session.
  const [knownProviders, setKnownProviders] = useState([]);
  const activeOrgCodeRef = useRef(null);

  const organizations = useSelector(
    (state) => state.orgAccount?.organizations
  );
  const orgCode = organizations?.[0]?.orgcode ?? null;

  // Keep toggle selection across page reload (AWS ↔ Azure)
  useEffect(() => {
    persistSelectedProvider(selectedProvider);
  }, [selectedProvider]);

  const resetCloudProviderState = useCallback(() => {
    setKnownProviders([]);
    setSelectedProvider("AWS");
  }, []);

  // Org switch / RESET_STORE: rebuild toggle state from the new org only
  useEffect(() => {
    if (!orgCode) {
      // Store reset or accounts not loaded yet — clear cross-org memory
      if (activeOrgCodeRef.current !== null) {
        resetCloudProviderState();
        activeOrgCodeRef.current = null;
      }
      return;
    }

    if (
      activeOrgCodeRef.current !== null &&
      activeOrgCodeRef.current !== orgCode
    ) {
      resetCloudProviderState();
    }
    activeOrgCodeRef.current = orgCode;
  }, [orgCode, resetCloudProviderState]);

  const registerObservedProviders = useCallback((accounts = []) => {
    const types = getDistinctExplicitCloudProviders(accounts);
    if (types.size === 0) return;
    setKnownProviders((prev) => {
      const next = new Set(prev);
      let changed = false;
      types.forEach((type) => {
        if (!next.has(type)) {
          next.add(type);
          changed = true;
        }
      });
      return changed ? Array.from(next) : prev;
    });
  }, []);

  const showCloudProviderToggle = knownProviders.length > 1;

  const value = useMemo(
    () => ({
      selectedProvider,
      setSelectedProvider,
      registerObservedProviders,
      resetCloudProviderState,
      showCloudProviderToggle,
      knownProviders,
    }),
    [
      selectedProvider,
      registerObservedProviders,
      resetCloudProviderState,
      showCloudProviderToggle,
      knownProviders,
    ]
  );

  return (
    <CloudProviderContext.Provider value={value}>
      {children}
    </CloudProviderContext.Provider>
  );
};

export const getDistinctExplicitCloudProviders = (accounts = []) => {
  const types = new Set();
  if (!Array.isArray(accounts)) {
    return types;
  }
  accounts.forEach((account) => {
    const type = getExplicitAccountCloudType(account);
    if (type) {
      types.add(type);
    }
  });
  return types;
};

export const shouldShowCloudProviderToggle = (accounts = []) => {
  return getDistinctExplicitCloudProviders(accounts).size > 1;
};

// When toggle is hidden: sole explicit type, or AWS if none / all null
export const getImplicitCloudProviderWhenToggleHidden = (accounts = []) => {
  const types = getDistinctExplicitCloudProviders(accounts);
  if (types.size === 1) {
    return [...types][0];
  }
  return "AWS";
};
